import * as React from 'react';

import { TextField, TextFieldProps } from '@rematter/ui-web';

type Maybe<T> = T | null | undefined;

const VEHICLE_VIN_LENGTH = 17;

const getDecodeVinValuesUrl = (vehicleVin: string) =>
  `https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVinValues/${vehicleVin}?format=json`;

type VehicleInfo = {
  ModelYear: string;
  Make: string;
  Model: string;
};

type VehicleInfoResult = {
  isValidVin: boolean;
  vehicleInfo?: VehicleInfo;
  error?: boolean | Record<string, unknown>;
};

const getVehicleInfoFromVin = async (
  vehicleVin: Maybe<string>,
): Promise<VehicleInfoResult> => {
  const isValidVin = !!vehicleVin && vehicleVin.length === VEHICLE_VIN_LENGTH;

  if (!isValidVin) {
    return { isValidVin };
  }

  try {
    const response = await fetch(getDecodeVinValuesUrl(vehicleVin));
    const result = await response.json();

    const vehicleInfo = result?.Results[0];

    return {
      isValidVin,
      vehicleInfo,
      error: response.status !== 200 ? (result ?? true) : undefined,
    };
  } catch (error) {
    return {
      isValidVin,
      error: error as Record<string, unknown>,
    };
  }
};

type VehicleVinTextFieldProps = Omit<
  TextFieldProps,
  'onChange' | 'className'
> & {
  onChange?: (value: string) => void;
  onSuccess?: (vehicleInfo: VehicleInfo) => void;
  className?: string;
};

export const VehicleVinTextField = ({
  onChange,
  onSuccess,
  ...props
}: VehicleVinTextFieldProps) => {
  const [isLoading, setIsLoading] = React.useState(false);

  const handleChange = async (value: string) => {
    onChange?.(value);

    if (value.length === VEHICLE_VIN_LENGTH) {
      setIsLoading(true);
      try {
        const result = await getVehicleInfoFromVin(value);
        if (result.isValidVin && result.vehicleInfo && onSuccess) {
          onSuccess(result.vehicleInfo);
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="@VehicleVinTextField relative">
      <TextField
        {...props}
        onChange={handleChange}
        isDisabled={isLoading}
        labelAdornment={
          isLoading && (
            <div className="size-4 animate-spin rounded-full border-b-2 border-neutral/90" />
          )
        }
      />
    </div>
  );
};
